import { useHandler } from "@redotech/react-util/hook";
import { useDebouncedLoad } from "@redotech/react-util/load";
import {
  FocusEvent,
  FocusEventHandler,
  KeyboardEvent,
  KeyboardEventHandler,
  RefObject,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface RedoTextareaValidationIssue {
  /** Inclusive start offset into the validated text. */
  start: number;
  /** Exclusive end offset into the validated text. */
  end: number;
  message: string;
  /** When set, a Fix action replaces the flagged range with this text. */
  replacement?: string;
}

export type RedoTextareaValidationStatus =
  | "idle"
  | "loading"
  | "valid"
  | "invalid";

export interface RedoTextareaValidation {
  /** Called after typing pauses. An empty result marks the text as valid. */
  validate(
    value: string,
    signal: AbortSignal,
  ): Promise<readonly RedoTextareaValidationIssue[]>;
  debounceMs?: number;
  onStatusChange?(status: RedoTextareaValidationStatus): void;
}

const DEFAULT_DEBOUNCE_MS = 500;

/**
 * Moves issues found in `validatedText` onto `currentText`, dropping any the edit touched.
 * Returns sorted, in-bounds, non-overlapping issues.
 */
export function alignIssuesToText<T extends RedoTextareaValidationIssue>(
  issues: readonly T[],
  validatedText: string,
  currentText: string,
): T[] {
  const maxPrefix = Math.min(validatedText.length, currentText.length);
  let prefix = 0;
  while (prefix < maxPrefix && validatedText[prefix] === currentText[prefix]) {
    prefix++;
  }
  let suffix = 0;
  while (
    suffix < maxPrefix - prefix &&
    validatedText[validatedText.length - 1 - suffix] ===
      currentText[currentText.length - 1 - suffix]
  ) {
    suffix++;
  }
  const editEnd = validatedText.length - suffix;
  const delta = currentText.length - validatedText.length;

  const aligned = issues
    .filter(
      (issue) =>
        issue.start >= 0 &&
        issue.start < issue.end &&
        issue.end <= validatedText.length,
    )
    .flatMap((issue) => {
      if (issue.end <= prefix) {
        return [issue];
      }
      if (issue.start >= editEnd) {
        return [
          { ...issue, start: issue.start + delta, end: issue.end + delta },
        ];
      }
      return [];
    })
    .sort((a, b) => a.start - b.start);

  let lastEnd = 0;
  return aligned.filter((issue) => {
    if (issue.start < lastEnd) {
      return false;
    }
    lastEnd = issue.end;
    return true;
  });
}

/** Identifies one flagged occurrence (the nth repeat of its text) so Ignore survives re-validation. */
export function issueIgnoreKey(
  issue: RedoTextareaValidationIssue,
  text: string,
): string {
  const flagged = text.slice(issue.start, issue.end);
  const occurrence = text.slice(0, issue.start).split(flagged).length - 1;
  return `${issue.message}\u0000${flagged}\u0000${occurrence}`;
}

export function useTextareaValidation(
  value: string,
  validation: RedoTextareaValidation | undefined,
): {
  status: RedoTextareaValidationStatus;
  issues: RedoTextareaValidationIssue[];
  /** True once any result exists; stays true while the next run is pending. */
  checked: boolean;
  ignore(issue: RedoTextareaValidationIssue): void;
} {
  const [ignoredKeys, setIgnoredKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const shouldValidate = !!validation && value.trim() !== "";

  const load = useDebouncedLoad(
    async (signal) =>
      validation && shouldValidate
        ? { text: value, issues: await validation.validate(value, signal) }
        : undefined,
    validation?.debounceMs ?? DEFAULT_DEBOUNCE_MS,
    [value],
    shouldValidate,
  );
  const result = load.value;

  const issues = useMemo(
    () =>
      shouldValidate && result
        ? alignIssuesToText(result.issues, result.text, value).filter(
            (issue) => !ignoredKeys.has(issueIgnoreKey(issue, value)),
          )
        : [],
    [shouldValidate, result, value, ignoredKeys],
  );

  const status: RedoTextareaValidationStatus = !shouldValidate
    ? "idle"
    : load.pending
      ? "loading"
      : !result
        ? "idle"
        : issues.length > 0
          ? "invalid"
          : "valid";

  const notifyStatus = useHandler((status: RedoTextareaValidationStatus) =>
    validation?.onStatusChange?.(status),
  );
  useEffect(() => {
    notifyStatus(status);
  }, [status, notifyStatus]);

  const ignore = useHandler((issue: RedoTextareaValidationIssue) =>
    setIgnoredKeys((keys) => new Set(keys).add(issueIgnoreKey(issue, value))),
  );

  return { status, issues, checked: shouldValidate && !!result, ignore };
}

const HOVER_OPEN_DELAY_MS = 150;
const HOVER_CLOSE_DELAY_MS = 250;

/** Which issue's popover is open (from the caret or the pointer) and how focus moves through it. */
export function useTextareaIssuePopover({
  value,
  setValue,
  issues,
  ignore,
  textareaRef,
  onBlur,
  onKeyDown,
}: {
  value: string;
  setValue(value: string): void;
  issues: readonly RedoTextareaValidationIssue[];
  ignore(issue: RedoTextareaValidationIssue): void;
  textareaRef: RefObject<HTMLTextAreaElement>;
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>;
}) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [caret, setCaret] = useState<number | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [hoveredIssue, setHoveredIssue] = useState<
    RedoTextareaValidationIssue | undefined
  >();
  const hoverTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  const caretIssue =
    caret === null || dismissed
      ? undefined
      : issues.find((issue) => issue.start <= caret && caret <= issue.end);
  const hovered =
    hoveredIssue && issues.includes(hoveredIssue) ? hoveredIssue : undefined;
  const issue = hovered ?? caretIssue;

  const close = useHandler(() => {
    clearTimeout(hoverTimer.current);
    setHoveredIssue(undefined);
    setDismissed(true);
  });

  const returnFocus = useHandler(() => {
    close();
    textareaRef.current?.focus();
  });

  const focusIsInside = useHandler(() => {
    const active = document.activeElement;
    return (
      active === textareaRef.current ||
      (!!active && !!popoverRef.current?.contains(active))
    );
  });

  const hoverIssue = useHandler(
    (next: RedoTextareaValidationIssue | undefined) => {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = setTimeout(
        () => setHoveredIssue(next),
        next ? HOVER_OPEN_DELAY_MS : HOVER_CLOSE_DELAY_MS,
      );
    },
  );

  const handleTextareaSelect = useHandler(
    (event: SyntheticEvent<HTMLTextAreaElement>) => {
      const { selectionStart, selectionEnd } = event.currentTarget;
      const next = selectionStart === selectionEnd ? selectionStart : null;
      if (next !== caret) {
        setDismissed(false);
      }
      setCaret(next);
    },
  );

  const handleTextareaBlur = useHandler(
    (event: FocusEvent<HTMLTextAreaElement>) => {
      const target = event.relatedTarget;
      if (!(target instanceof Node && popoverRef.current?.contains(target))) {
        setCaret(null);
      }
      onBlur?.(event);
    },
  );

  const handleTextareaKeyDown = useHandler(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      const firstButton = popoverRef.current?.querySelector("button");
      if (issue && event.key === "Escape") {
        event.stopPropagation();
        close();
      } else if (event.key === "Tab" && !event.shiftKey && firstButton) {
        event.preventDefault();
        firstButton.focus();
      }
      onKeyDown?.(event);
    },
  );

  const fix = useHandler((fixed: RedoTextareaValidationIssue) => {
    const replacement = fixed.replacement ?? "";
    const nextCaret = fixed.start + replacement.length;
    const hadFocus = focusIsInside();
    setValue(
      value.slice(0, fixed.start) + replacement + value.slice(fixed.end),
    );
    close();
    if (hadFocus) {
      textareaRef.current?.focus();
      requestAnimationFrame(() =>
        textareaRef.current?.setSelectionRange(nextCaret, nextCaret),
      );
    }
  });

  const ignoreAndClose = useHandler((ignored: RedoTextareaValidationIssue) => {
    const hadFocus = focusIsInside();
    ignore(ignored);
    close();
    if (hadFocus) {
      textareaRef.current?.focus();
    }
  });

  const handlePopoverKeyDown = useHandler(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const buttons = Array.from(
        popoverRef.current?.querySelectorAll("button") ?? [],
      );
      const edge = event.shiftKey ? buttons[0] : buttons[buttons.length - 1];
      if (event.key === "Escape") {
        event.stopPropagation();
        returnFocus();
      } else if (
        event.key === "Tab" &&
        edge &&
        document.activeElement === edge
      ) {
        event.preventDefault();
        returnFocus();
      }
    },
  );

  const handlePopoverBlur = useHandler((event: FocusEvent<HTMLDivElement>) => {
    const target = event.relatedTarget;
    const staysInside =
      target instanceof Node &&
      (target === textareaRef.current || popoverRef.current?.contains(target));
    if (!staysInside) {
      setCaret(null);
      close();
    }
  });

  const keepHoverOpen = useHandler(() => clearTimeout(hoverTimer.current));
  const releaseHover = useHandler(() => hoverIssue(undefined));

  const popoverHandlers = useMemo(
    () => ({
      onFix: fix,
      onIgnore: ignoreAndClose,
      onKeyDown: handlePopoverKeyDown,
      onBlur: handlePopoverBlur,
      onMouseEnter: keepHoverOpen,
      onMouseLeave: releaseHover,
    }),
    [
      fix,
      ignoreAndClose,
      handlePopoverKeyDown,
      handlePopoverBlur,
      keepHoverOpen,
      releaseHover,
    ],
  );

  return {
    issue,
    caretIssue,
    popoverRef,
    hoverIssue,
    textareaHandlers: {
      onSelect: handleTextareaSelect,
      onBlur: handleTextareaBlur,
      onKeyDown: handleTextareaKeyDown,
    },
    popoverHandlers,
  };
}
